import React from 'react';
import { Link } from 'react-router-dom';
import { IconBuildingSkyscraper, IconTargetArrow, IconUsersGroup, IconNetwork } from '@tabler/icons-react';
import partnerContent from '../data/partners.json';

const whyPartnerIcons = [
  IconBuildingSkyscraper,
  IconTargetArrow,
  IconNetwork,
  IconUsersGroup,
];

const PartnerWithOffisbay = () => {
  const { hero, whyPartnerWithUs, partners, partnerCategories, partnershipOpportunities, finalCta } = partnerContent;

  return (
    <div className="bg-[#FCFAF8] text-[#152238]">
      <section className="relative overflow-hidden  px-6 py-16 md:py-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(150,99,118,0.12),_transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto text-center">
          <p className="text-sm uppercase tracking-[0.18em] text-[#966376] font-semibold mt-8 ">Business Ecosystem</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-regrade font-bold leading-tight">{hero.title}</h1>
          <p className="mt-6 text-base md:text-lg text-[#44536A] max-w-3xl mx-auto leading-relaxed">{hero.subtext}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to={hero.primaryCta.href}
              className="inline-flex items-center justify-center rounded-full bg-[#966376] px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#7d5262]"
            >
              {hero.primaryCta.label}
            </Link>
            <a
              href={hero.secondaryCta.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-full border border-[#1F2E46] px-7 py-3 text-sm font-semibold text-[#1F2E46] transition-colors hover:bg-[#1F2E46] hover:text-white"
            >
              {hero.secondaryCta.label}
            </a>
          </div>
        </div>
      </section>

      <section className="px-6 py-14 md:py-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-regrade font-bold text-center">Why Partner With Us</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whyPartnerWithUs.map((item, index) => {
              const Icon = whyPartnerIcons[index % whyPartnerIcons.length];
              return (
                <article key={item.title} className="rounded-2xl border border-[#E6DFDA] bg-white p-6 shadow-[0_10px_30px_rgba(21,34,56,0.05)]">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#F6EEF2] text-[#966376]">
                    <Icon size={22} strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#4C5A70]">{item.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-6 py-14 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <p className="text-sm uppercase tracking-[0.18em] text-[#966376] font-semibold">Our Partner Ecosystem</p>
            <h2 className="mt-3 text-2xl md:text-4xl font-regrade font-bold">Trusted Partners Creating Business Value</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partners.map((partner) => (
              <a
                key={partner.name}
                href={partner.website}
                target="_blank"
                rel="noreferrer"
                className="group rounded-2xl border border-[#E8E2DD] bg-[#FFFDFA] p-6 transition-all hover:shadow-[0_18px_35px_rgba(21,34,56,0.08)]"
              >
                <div className="h-[88px] w-full flex items-center justify-start">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-full w-auto max-w-full object-contain"
                  />
                </div>
                <h3 className="mt-5 text-xl font-semibold text-[#13213A]">{partner.name}</h3>
                {partner.tagline && (
                  <p className="mt-1 text-[11px] tracking-[0.14em] uppercase text-[#2A3650] font-semibold">
                    {partner.tagline}
                  </p>
                )}
                <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[#966376] font-semibold">{partner.category}</p>
                <p className="mt-3 text-sm leading-relaxed text-[#4C5A70]">{partner.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-14 md:py-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="rounded-2xl border border-[#E8E2DD] bg-white p-7">
            <h2 className="text-2xl md:text-3xl font-regrade font-bold">Who We Partner With</h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {partnerCategories.map((category) => (
                <span key={category} className="rounded-full border border-[#DCCEC6] px-4 py-2 text-sm text-[#1F2E46] bg-[#FCFAF8]">
                  {category}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-[#E8E2DD] bg-white p-7">
            <h2 className="text-2xl md:text-3xl font-regrade font-bold">Partnership Opportunities</h2>
            <ul className="mt-6 space-y-3">
              {partnershipOpportunities.map((opportunity) => (
                <li key={opportunity} className="flex items-start gap-3 text-[#24344D]">
                  <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#966376]" />
                  <span className="text-sm md:text-base">{opportunity}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 md:pb-20">
        <div className="max-w-5xl mx-auto rounded-3xl bg-[#152238] px-8 py-12 text-center text-white">
          <h2 className="text-3xl md:text-5xl font-regrade font-bold">{finalCta.title}</h2>
          <Link
            to={finalCta.buttonHref}
            className="mt-8 inline-flex items-center justify-center rounded-full bg-white px-8 py-3 text-sm font-semibold text-[#152238] transition-transform hover:scale-[1.03]"
          >
            {finalCta.buttonLabel}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PartnerWithOffisbay;
